/**
 * =====================================================================================
 * SERVICIO DE CATÁLOGOS DEL MINISTERIO DE HACIENDA - catalogos.service.js
 * =====================================================================================
 *
 * Este servicio obtiene los catálogos oficiales del Ministerio de Hacienda (MH)
 * de El Salvador. Los catálogos son tablas de referencia estandarizadas que
 * contienen códigos y valores válidos para los documentos tributarios electrónicos (DTE).
 *
 * ¿QUÉ SON LOS CATÁLOGOS DEL MH?
 * ------------------------------
 * El Ministerio de Hacienda define catálogos con códigos oficiales que deben usarse
 * en los documentos electrónicos. Por ejemplo:
 *
 * - CAT-002: Tipos de Documento (Factura=01, CCF=03, etc.)
 * - CAT-012: Departamentos de El Salvador (San Salvador=06, La Libertad=05, etc.)
 * - CAT-013: Municipios de El Salvador
 * - CAT-014: Unidades de Medida (Unidad=59, Docena=13, etc.)
 * - CAT-017: Formas de Pago (Efectivo=01, Cheque=02, etc.)
 * - CAT-019: Actividades Económicas
 * - CAT-022: Tributos (IVA, CESC, etc.)
 *
 * Estos códigos son OBLIGATORIOS. Si enviamos un código no válido o no reconocido,
 * Hacienda rechaza el documento.
 *
 * ARQUITECTURA DE DATOS:
 * ----------------------
 *
 *   Backend (BD PostgreSQL)          Frontend (Vuex Store)
 *   ┌─────────────────────┐          ┌─────────────────────┐
 *   │ Tabla: cat_tipos_   │          │ store/catalogos.js  │
 *   │ documento           │          │ state.tiposDocumento│
 *   │ - id: 1             │  ───►    │ - id: 1             │
 *   │ - codigo: "01"      │          │ - codigo: "01"      │
 *   │ - valor: "Factura"  │          │ - valor: "Factura"  │
 *   └─────────────────────┘          └─────────────────────┘
 *
 * El backend almacena estos catálogos en su base de datos y los expone
 * vía API. El frontend los carga al iniciar la app y los guarda en Vuex
 * para acceso rápido.
 *
 * FLUJO DE CARGA DE CATÁLOGOS:
 * ----------------------------
 *
 *   App.vue (onMounted)
 *         ↓
 *   store.dispatch('catalogos/cargarCatalogos')
 *         ↓
 *   catalogosService.getDepartamentos() // y demás métodos
 *         ↓
 *   GET /api/Catalogos/departamentos
 *         ↓
 *   Backend consulta tabla cat_departamentos
 *         ↓
 *   Respuesta: [{id:1, codigo:"01", valor:"Ahuachapán"}, ...]
 *         ↓
 *   Store guarda en state.departamentos
 *         ↓
 *   Componentes usan: store.state.catalogos.departamentos
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - api.js: Cliente HTTP para las peticiones
 * - src/store/modules/catalogos.js: Almacena los catálogos en estado global
 * - src/views/emision/*.vue: Usan los catálogos en formularios (selects)
 * - src/services/facturas.service.js: Mapea códigos MH a IDs de BD
 *
 * =====================================================================================
 */

// Importamos la instancia de Axios configurada
import api from './api'

/**
 * =====================================================================================
 * OBJETO DEL SERVICIO DE CATÁLOGOS
 * =====================================================================================
 *
 * Cada método corresponde a un catálogo específico del MH.
 * Todos siguen el mismo patrón:
 * 1. Hacer GET al endpoint correspondiente
 * 2. Retornar el array de datos
 *
 * ESTRUCTURA DE RESPUESTA (típica):
 * [
 *   { id: 1, codigo: "01", valor: "Descripción" },
 *   { id: 2, codigo: "02", valor: "Otra descripción" },
 *   ...
 * ]
 *
 * Donde:
 * - id: Identificador interno en la base de datos (para relaciones FK)
 * - codigo: Código oficial del MH (se envía en los DTEs)
 * - valor: Descripción legible para el usuario
 */
const catalogosService = {
  /**
   * ===========================================================================
   * CAT-012: DEPARTAMENTOS DE EL SALVADOR
   * ===========================================================================
   *
   * Los 14 departamentos del país con sus códigos oficiales:
   * 01=Ahuachapán, 02=Santa Ana, 03=Sonsonate, 04=Chalatenango,
   * 05=La Libertad, 06=San Salvador, 07=Cuscatlán, 08=La Paz,
   * 09=Cabañas, 10=San Vicente, 11=Usulután, 12=San Miguel,
   * 13=Morazán, 14=La Unión
   *
   * USO: Dirección del emisor y receptor en el DTE
   *
   * @returns {Promise<Array>} Lista de departamentos [{id, codigo, valor}]
   */
  async getDepartamentos() {
    const response = await api.get('/Catalogos/departamentos')
    return response.data
  },

  /**
   * ===========================================================================
   * CAT-013: MUNICIPIOS DE EL SALVADOR
   * ===========================================================================
   *
   * Los 262 municipios del país. Cada municipio está asociado a un departamento.
   *
   * ESTRUCTURA DE RESPUESTA:
   * {
   *   id: 1,
   *   codigo: "01",           // Código del municipio dentro del departamento
   *   valor: "Ahuachapán",    // Nombre del municipio
   *   codigoDepartamento: "01" // Departamento al que pertenece
   * }
   *
   * USO: Dirección del emisor y receptor en el DTE
   *
   * @returns {Promise<Array>} Lista de todos los municipios
   */
  async getMunicipios() {
    const response = await api.get('/Catalogos/municipios')
    return response.data
  },

  /**
   * ===========================================================================
   * MUNICIPIOS FILTRADOS POR DEPARTAMENTO
   * ===========================================================================
   *
   * Método de utilidad que filtra los municipios por departamento.
   * Útil para selectores en cascada: primero seleccionar departamento,
   * luego mostrar solo los municipios de ese departamento.
   *
   * FLUJO EN LA UI:
   * Usuario selecciona departamento "06" (San Salvador)
   *        ↓
   * getMunicipiosByDepartamento("06")
   *        ↓
   * Retorna solo: San Salvador, Apopa, Mejicanos, Soyapango, etc.
   *
   * @param {string} codigoDepartamento - Código del departamento (ej: "06")
   * @returns {Promise<Array>} Municipios filtrados
   */
  async getMunicipiosByDepartamento(codigoDepartamento) {
    // Primero obtenemos TODOS los municipios
    const municipios = await this.getMunicipios()
    // Luego filtramos solo los del departamento especificado
    // El método filter() crea un nuevo array con los elementos que pasan el test
    return municipios.filter(m => m.codigoDepartamento === codigoDepartamento)
  },

  /**
   * ===========================================================================
   * CAT-008: DISTRITOS DE EL SALVADOR
   * ===========================================================================
   *
   * Nivel territorial nuevo de la Normativa V2.0. Cada distrito (antes municipio)
   * pertenece a un municipio agrupado (CAT-013) dentro de un departamento.
   *
   * ESTRUCTURA DE RESPUESTA:
   * {
   *   id: 2,
   *   codigo: "01",            // Código del distrito dentro del departamento
   *   valor: "Ahuachapán",     // Nombre del distrito
   *   codigoDepartamento: "01",
   *   codigoMunicipio: "14"    // Municipio agrupado (CAT-013) al que pertenece
   * }
   *
   * USO: Dirección del emisor y receptor en el DTE (obligatorio en V2.0).
   *
   * @returns {Promise<Array>} Lista de todos los distritos
   */
  async getDistritos() {
    const response = await api.get('/Catalogos/distritos')
    return response.data
  },

  /**
   * Distritos filtrados por departamento + municipio (para la cascada
   * Departamento → Municipio → Distrito).
   *
   * @param {string} codigoDepartamento
   * @param {string} codigoMunicipio
   * @returns {Promise<Array>} Distritos del municipio indicado
   */
  async getDistritosByMunicipio(codigoDepartamento, codigoMunicipio) {
    const distritos = await this.getDistritos()
    return distritos.filter(
      d => d.codigoDepartamento === codigoDepartamento && d.codigoMunicipio === codigoMunicipio
    )
  },

  /**
   * ===========================================================================
   * CAT-019: ACTIVIDADES ECONÓMICAS
   * ===========================================================================
   *
   * Clasificación de actividades económicas según el MH.
   * Cada empresa tiene una o más actividades económicas registradas.
   *
   * EJEMPLOS:
   * - "46101": Venta al por mayor de computadores
   * - "47111": Venta al por menor en supermercados
   * - "62010": Programación informática
   *
   * USO: Se incluye en datos del emisor y opcionalmente del receptor
   *
   * @returns {Promise<Array>} Lista de actividades económicas
   */
  async getActividadesEconomicas() {
    const response = await api.get('/Catalogos/actividades-economicas')
    return response.data
  },

  /**
   * ===========================================================================
   * CAT-022: TRIBUTOS
   * ===========================================================================
   *
   * Impuestos aplicables en los documentos tributarios.
   *
   * PRINCIPALES TRIBUTOS:
   * - "20": IVA 13% (Impuesto al Valor Agregado)
   * - "C3": CESC (Contribución Especial para Seguridad Ciudadana)
   * - "59": FOVIAL (Fondo de Conservación Vial)
   * - "D1": COTRANS
   *
   * USO: Detalle de tributos aplicados a cada ítem del documento
   *
   * @returns {Promise<Array>} Lista de tributos disponibles
   */
  async getTributos() {
    const response = await api.get('/Catalogos/tributos')
    return response.data
  },

  /**
   * ===========================================================================
   * CAT-014: UNIDADES DE MEDIDA
   * ===========================================================================
   *
   * Unidades para medir cantidades de productos/servicios.
   *
   * EJEMPLOS COMUNES:
   * - "59": Unidad (la más común)
   * - "27": Paquete
   * - "13": Docena
   * - "07": Kilogramo
   * - "17": Metro
   *
   * USO: Cada línea del cuerpo del documento especifica la unidad de medida
   *
   * @returns {Promise<Array>} Lista de unidades de medida
   */
  async getUnidadesMedida() {
    const response = await api.get('/Catalogos/unidades-medida')
    return response.data
  },

  /**
   * ===========================================================================
   * CAT-002: TIPOS DE DOCUMENTO TRIBUTARIO
   * ===========================================================================
   *
   * Los diferentes tipos de documentos tributarios electrónicos (DTE).
   *
   * TIPOS PRINCIPALES:
   * - "01": Factura de Consumidor Final
   * - "03": Comprobante de Crédito Fiscal (CCF)
   * - "05": Nota de Crédito
   * - "06": Nota de Débito
   * - "11": Factura de Exportación
   * - "14": Factura Sujeto Excluido
   *
   * USO: Define qué tipo de documento se está emitiendo
   * Afecta la estructura y validaciones del documento
   *
   * @returns {Promise<Array>} Lista de tipos de documento
   */
  async getTiposDocumento() {
    const response = await api.get('/Catalogos/tipos-documento')
    return response.data
  },

  /**
   * ===========================================================================
   * CAT-017: FORMAS DE PAGO
   * ===========================================================================
   *
   * Métodos de pago aceptados.
   *
   * FORMAS DE PAGO:
   * - "01": Billetes y monedas (efectivo)
   * - "02": Tarjeta de débito
   * - "03": Tarjeta de crédito
   * - "04": Cheque
   * - "05": Transferencia/Depósito bancario
   * - "06": Vales
   * - "99": Otros
   *
   * USO: Sección de resumen/pago del documento
   *
   * @returns {Promise<Array>} Lista de formas de pago
   */
  async getFormasPago() {
    const response = await api.get('/Catalogos/formas-pago')
    return response.data
  },

  /**
   * ===========================================================================
   * CAT-016: CONDICIONES DE OPERACIÓN
   * ===========================================================================
   *
   * Condiciones bajo las cuales se realiza la operación comercial.
   *
   * CONDICIONES:
   * - "1": Contado (pago inmediato)
   * - "2": Crédito (pago a plazos)
   * - "3": Otro
   *
   * USO: Sección de resumen del documento
   * Si es crédito, se debe especificar plazo de pago
   *
   * NOTA: El endpoint tiene un typo "condiciones-operacione" (sin 's' final)
   * que debe coincidir con el backend
   *
   * @returns {Promise<Array>} Lista de condiciones de operación
   */
  async getCondicionesOperacion() {
    const response = await api.get('/Catalogos/condiciones-operacione')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE ESTABLECIMIENTO
   * ===========================================================================
   *
   * Clasificación del tipo de establecimiento/punto de venta.
   *
   * TIPOS:
   * - "01": Sucursal/Agencia
   * - "02": Casa matriz
   * - "04": Bodega
   * - "20": Punto de venta
   *
   * USO: Identificación del establecimiento emisor
   *
   * @returns {Promise<Array>} Lista de tipos de establecimiento
   */
  async getTiposEstablecimiento() {
    const response = await api.get('/Catalogos/tipos-establecimiento')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE TRANSMISIÓN
   * ===========================================================================
   *
   * Indica cómo se transmite el documento al MH.
   *
   * TIPOS:
   * - "1": Normal (transmisión en tiempo real)
   * - "2": Contingencia (transmisión diferida por problemas técnicos)
   *
   * USO: Sección de identificación del documento
   *
   * @returns {Promise<Array>} Lista de tipos de transmisión
   */
  async getTiposTransmision() {
    const response = await api.get('/Catalogos/tipos-transmision')
    return response.data
  },

  /**
   * ===========================================================================
   * MODELOS DE FACTURACIÓN
   * ===========================================================================
   *
   * Modelo de operación del sistema de facturación.
   *
   * MODELOS:
   * - "1": Previo (el documento se envía ANTES de la entrega del bien/servicio)
   * - "2": Diferido (el documento se envía DESPUÉS de la operación)
   *
   * La mayoría de empresas usan el modelo Previo (1).
   *
   * @returns {Promise<Array>} Lista de modelos de facturación
   */
  async getModelosFacturacion() {
    const response = await api.get('/Catalogos/modelos-facturacion')
    return response.data
  },

  /**
   * ===========================================================================
   * AMBIENTES DE DESTINO
   * ===========================================================================
   *
   * Ambiente donde se envía el documento.
   *
   * AMBIENTES:
   * - "00": Pruebas (para desarrollo y testing)
   * - "01": Producción (documentos reales con validez fiscal)
   *
   * IMPORTANTE: Los documentos enviados a producción tienen validez legal
   * y tributaria. En pruebas, los documentos no tienen efectos fiscales.
   *
   * @returns {Promise<Array>} Lista de ambientes
   */
  async getAmbientesDestino() {
    const response = await api.get('/Catalogos/ambientes-destino')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE CONTINGENCIA
   * ===========================================================================
   *
   * Motivos por los cuales se emite un documento en contingencia.
   *
   * La contingencia ocurre cuando no es posible transmitir en tiempo real
   * al Ministerio de Hacienda (por problemas de internet, sistema MH caído, etc.)
   *
   * TIPOS:
   * - "1": No disponibilidad de sistema del MH
   * - "2": Fuerza mayor
   * - "3": Sistema del contribuyente en mantenimiento
   *
   * @returns {Promise<Array>} Lista de tipos de contingencia
   */
  async getTiposContingencia() {
    const response = await api.get('/Catalogos/tipos-contingencias')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE DOCUMENTO EN CONTINGENCIA
   * ===========================================================================
   *
   * Qué tipos de documento se pueden emitir en contingencia.
   * Similar a CAT-002 pero específico para escenario de contingencia.
   *
   * @returns {Promise<Array>} Lista de tipos de documento contingencia
   */
  async getTiposDocumentoContingencia() {
    const response = await api.get('/Catalogos/tipos-documento-contingencia')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE DOCUMENTO DE IDENTIFICACIÓN DEL RECEPTOR
   * ===========================================================================
   *
   * Documentos con los que se puede identificar al receptor/cliente.
   *
   * TIPOS PRINCIPALES:
   * - "13": DUI (Documento Único de Identidad)
   * - "36": NIT (Número de Identificación Tributaria)
   * - "37": Otro
   * - "02": Pasaporte
   * - "03": Carné de residente
   *
   * USO: Sección del receptor en el documento
   * El tipo determina el formato esperado del número de documento
   *
   * @returns {Promise<Array>} Lista de tipos de documento de identificación
   */
  async getTiposDocumentoIdentificacionReceptor() {
    const response = await api.get('/Catalogos/tipos-documento-identificacion-receptor')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE GENERACIÓN DE DOCUMENTO
   * ===========================================================================
   *
   * Indica cómo fue generado el número de documento.
   *
   * TIPOS:
   * - "1": Por el sistema de la empresa
   * - "2": Por el contribuyente (manual)
   *
   * @returns {Promise<Array>} Lista de tipos de generación
   */
  async getTiposGeneracionDocumento() {
    const response = await api.get('/Catalogos/tipos-generacion-documentos')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE INVALIDACIÓN
   * ===========================================================================
   *
   * Motivos para invalidar (anular) un documento ya emitido.
   *
   * TIPOS:
   * - "1": Error en información del documento
   * - "2": Rescisión de la operación (cancelación de la venta)
   * - "3": Otro
   *
   * USO: Proceso de anulación de facturas
   *
   * @returns {Promise<Array>} Lista de tipos de invalidación
   */
  async getTiposInvalidacion() {
    const response = await api.get('/Catalogos/tipos-invalidacion')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE ÍTEM
   * ===========================================================================
   *
   * Clasificación de los productos o servicios vendidos.
   *
   * TIPOS:
   * - "1": Bien (producto físico)
   * - "2": Servicio (intangible)
   * - "3": Ambos (bien + servicio)
   * - "4": Otro
   *
   * USO: Cada línea del cuerpo del documento indica si es bien o servicio
   *
   * @returns {Promise<Array>} Lista de tipos de ítem
   */
  async getTiposItem() {
    const response = await api.get('/Catalogos/tipos-items')
    return response.data
  },

  /**
   * ===========================================================================
   * PLAZOS DE PAGO
   * ===========================================================================
   *
   * Plazos disponibles para pagos a crédito.
   *
   * PLAZOS TÍPICOS:
   * - "01": 8 días
   * - "02": 15 días
   * - "03": 30 días
   * - "04": 60 días
   * - "05": 90 días
   *
   * USO: Cuando la condición de operación es "crédito" (2)
   *
   * @returns {Promise<Array>} Lista de plazos de pago
   */
  async getPlazos() {
    const response = await api.get('/Catalogos/plazos')
    return response.data
  },

  /**
   * ===========================================================================
   * OTROS DOCUMENTOS ASOCIADOS
   * ===========================================================================
   *
   * Tipos de documentos que pueden asociarse a un DTE.
   *
   * EJEMPLOS:
   * - Orden de compra
   * - Contrato
   * - Documento previo
   *
   * USO: Sección de documentos relacionados (opcional)
   *
   * @returns {Promise<Array>} Lista de tipos de documentos asociados
   */
  async getOtrosDocumentosAsociados() {
    const response = await api.get('/Catalogos/otros-documentos-asociados')
    return response.data
  },

  /**
   * ===========================================================================
   * TIPOS DE SERVICIO
   * ===========================================================================
   *
   * Clasificación de tipos de servicios médicos o especiales.
   * Se usa principalmente para documentos del sector salud.
   *
   * @returns {Promise<Array>} Lista de tipos de servicio
   */
  async getTiposServicio() {
    const response = await api.get('/Catalogos/tipos-servicios')
    return response.data
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL SERVICIO
 * =====================================================================================
 *
 * USO EN OTROS ARCHIVOS:
 * ----------------------
 * import catalogosService from '@/services/catalogos.service'
 *
 * // Obtener departamentos
 * const departamentos = await catalogosService.getDepartamentos()
 *
 * // Obtener municipios de un departamento
 * const municipiosSS = await catalogosService.getMunicipiosByDepartamento("06")
 *
 * ARCHIVOS QUE USAN ESTE SERVICIO:
 * --------------------------------
 * - src/store/modules/catalogos.js → Carga y almacena todos los catálogos
 * - src/views/emision/FacturaView.vue → Usa catálogos en formularios
 * - src/views/configuracion/ReceptoresView.vue → Tipos de documento
 * - src/components/emision/DatosGeneralesFactura.vue → Selects de catálogos
 */
export default catalogosService
